import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { BARCODE_SHEET_METRICS } from 'core/slices/utils/barcodeSheet';
import { LIGHT_TOKENS } from '../../../theme/tokens';
import AppText from '../../utils/AppText';

// The sheet always uses the light design tokens, never the active theme: a
// barcode needs a bright surface to scan. `colorScheme: 'light'` also stops the
// browser or an OS-level forced dark mode from inverting it.
const SCAN_SURFACE = LIGHT_TOKENS.colors.surface;
const SCAN_INK = LIGHT_TOKENS.colors.textPrimary;
const SCAN_SUBTLE = LIGHT_TOKENS.colors.textSecondary;
const styles = {
  // The trigger lives on a themed surface, so unlike the sheet it reads the live
  // accent token. Icon only, so the accessible name comes from aria-label.
  // Accent tints the background and paints the glyph: both are non-text and
  // clear the 3:1 minimum on every surface the button sits on, while accent
  // measures 3.02:1 on white and would fail the 4.5:1 text minimum.
  trigger: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, p: 0, borderRadius: 1.5, cursor: 'pointer',
    border: 'none',
    bgcolor: theme => alpha(theme.bruno.status.accent, 0.12),
    '&:hover': { bgcolor: theme => alpha(theme.bruno.status.accent, 0.22) },
  },
  triggerIcon: { fontSize: 15, color: theme => theme.bruno.status.accent },
  dialogPaper: {
    bgcolor: SCAN_SURFACE,
    colorScheme: 'light',
    borderRadius: 3,
    m: 1,
  },
  sheet: {
    bgcolor: SCAN_SURFACE, colorScheme: 'light',
    px: 3, pb: 3, pt: 2,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  // The close control is the whole header now: the caption inside the card names
  // the sheet, so a title above it would say the same thing twice.
  sheetHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
    width: '100%', mb: 1,
  },
  // The scan plate is the bright area the scanner actually sees. Pure white and
  // as large as the dialog allows: the browser cannot raise the backlight, so
  // emitted area is the only lever available. Deliberately borderless, so
  // nothing competes with the bars for the scanner's attention.
  scanPlate: {
    width: '100%', bgcolor: SCAN_SURFACE, borderRadius: 2,
    py: 2.5, px: 2.5,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  barcodeImage: {
    width: '100%', maxHeight: 220, objectFit: 'contain',
    mt: 2,
    // Belt and braces against a user stylesheet or an extension inverting the
    // page: the one element that must never be inverted says so itself.
    filter: 'none',
  },
  // Plain CSS, not `sx` objects: these are handed to AppText's `style`.
  caption: {
    color: SCAN_SUBTLE,
    letterSpacing: `${BARCODE_SHEET_METRICS.captionTracking}px`,
    textAlign: 'center',
  },
  productName: {
    marginTop: 2,
    color: SCAN_INK,
    fontWeight: 700,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  identifier: {
    marginTop: 16,
    color: SCAN_INK,
    letterSpacing: `${BARCODE_SHEET_METRICS.digitsTracking}px`,
  },
};

/**
 * Barcode affordance for the task detail header.
 *
 * Renders nothing unless the chain has the `barcode` task-detail feature
 * enabled AND this task type actually has a barcode asset.
 *
 * While the sheet is open it holds a screen wake lock where the browser
 * supports one, so the display does not dim mid-scan.
 */
export default function TaskBarcodeButton({ barcodeUrl, identifier, productName }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wakeLockRef = useRef(null);

  const releaseWakeLock = useCallback(() => {
    const lock = wakeLockRef.current;
    wakeLockRef.current = null;
    lock?.release?.().catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) {
      releaseWakeLock();
      return undefined;
    }
    let cancelled = false;
    navigator.wakeLock?.request('screen')
      .then(lock => {
        // The dialog may have closed while the promise was in flight.
        if (cancelled) lock.release().catch(() => {});
        else wakeLockRef.current = lock;
      })
      // Denied by policy, unsupported, or the tab lost visibility. The sheet is
      // still perfectly usable without it, so this is not worth surfacing.
      .catch(() => {});
    return () => {
      cancelled = true;
      releaseWakeLock();
    };
  }, [open, releaseWakeLock]);

  if (!barcodeUrl) return null;

  const accessibleName = t('bruno.tasks_details.show_barcode', 'Show barcode');

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        aria-label={accessibleName}
        sx={styles.trigger}
      >
        <QrCodeScannerIcon sx={styles.triggerIcon} />
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-label={accessibleName}
        PaperProps={{ sx: styles.dialogPaper }}
      >
        <Box sx={styles.sheet}>
          <Box sx={styles.sheetHeader}>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label={t('bruno.tasks_details.close', 'Close')}>
              <CloseIcon fontSize="small" sx={{ color: SCAN_INK }} />
            </IconButton>
          </Box>

          <Box sx={styles.scanPlate}>
            {/* `capsLabel` already uppercases and tracks; the colour is
                overridden because the variant's muted token only reaches 3.28:1
                on this surface. */}
            <AppText variant="capsLabel" style={styles.caption}>
              {t('bruno.tasks_details.barcode_scan_caption', 'Scan with device')}
            </AppText>
            {productName ? (
              <AppText variant="cardTitle" style={styles.productName}>
                {productName}
              </AppText>
            ) : null}

            <Box component="img" src={barcodeUrl} alt={accessibleName} sx={styles.barcodeImage} />
            {/* The barcode image carries no human-readable digits, so the
                identifier is
                printed here for manual keying. */}
            {/* Inline style rather than a Tailwind colour class: `countLarge`
                bakes in `text-primary`, and Tailwind emits colour utilities
                alphabetically, so a colour class would lose the cascade. */}
            {identifier?.value ? (
              <AppText variant="countLarge" style={styles.identifier}>
                {String(identifier.value)}
              </AppText>
            ) : null}
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
